import {StringIndex} from "../interfaces/generic/StringIndex";
import {Model} from "../models/Model";
import {ModelKey} from "../models/ModelKey";
import {IController, IT_WINDOW} from "./IController";
import {ControllerEventBus} from "../event-buses/controller.event-bus";
import {ControllerEvent} from "../event-buses/ControllerEvent";
import {inject, Injectable, OnDestroy} from "@angular/core";
import {debounceTime, fromEvent, Observable, Subscription} from "rxjs";
import {GoogleAnalyticsService} from "../services/google-analytics.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {distinctUntilChanged} from "rxjs/operators";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {TranslateService} from "@ngx-translate/core";
import {UserSession} from "../interfaces/domain/UserSession";
import {AccessControlService} from "../services/access-control.service";
import {MainEventBus} from "../event-buses/main.event-bus";
import {MainEvent} from "../event-buses/MainEvent";
import {CommonConstants} from "../constants/CommonConstants";
import {ToastMessageService} from "../services/toast-message.service";
import {UserTokensRepository} from "../repositories/user-tokens.repository";

@Injectable()
export abstract class AbstractController implements IController, OnDestroy {

  private modelMap: Map<string, Model> = new Map();
  private subscriptions: Array<Subscription> = [];

  private _document = inject(DOCUMENT);
  private _window = inject(IT_WINDOW);

  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  private _userSessionRepository = inject(UserSessionRepository);
  private _userTokensRepository = inject(UserTokensRepository);
  private _translateService = inject(TranslateService);
  private _accessControlService = inject(AccessControlService);
  private _toastMessageService = inject(ToastMessageService);

  protected readonly gaService: GoogleAnalyticsService;
  private readonly controllerEventBus: ControllerEventBus|undefined|null;
  private readonly mainEventBus: MainEventBus|undefined|null;

  protected constructor(controllerEventBus?: ControllerEventBus) {
    this.controllerEventBus = controllerEventBus;
    this.mainEventBus = inject(MainEventBus, {optional: true});
    this.gaService = inject(GoogleAnalyticsService);
  }

  addModel<T extends Model>(modelKey: ModelKey<T>, model: T): T {
    if(this.modelMap.has(modelKey.key())) {
      throw new Error(`Cannot add duplicate model (${modelKey.key()})`);
    }

    this.modelMap.set(modelKey.key(), model);

    return model;
  }

  getModel<T extends Model>(modelKey: ModelKey<T>): T {
    if(this.modelMap.has(modelKey.key())) {
      return <T>this.modelMap.get(modelKey.key());
    }
    else {
      throw new Error(`No model found for key: ${modelKey.key()}`);
    }
  }

  getDocument(): Document {
    return this._document;
  }

  getWindow(): Window {
    return this._window;
  }

  emit(event: ControllerEvent|MainEvent): void {
    if(event instanceof ControllerEvent) {
      if(this.controllerEventBus) {
        this.controllerEventBus.dispatch(event);
      }
      else {
        console.warn('No controller event bus is available');
      }
    }
    else {
      if(this.mainEventBus) {
        this.mainEventBus.dispatch(event);
      }
      else {
        console.warn('No main event bus is available');
      }
    }
  }

  protected subscribe(controllerEventName: string, handler: (event: ControllerEvent) => void, silent: boolean = false) {
    this.sub(this.eventBus().subscribe(controllerEventName, handler, silent));
  }

  protected subscribeMain(mainEventName: string, handler: (event: MainEvent) => void) {
    this.sub(this.eventBusMain().subscribe(mainEventName, handler));
  }

  protected subscribeWithDebounce(controllerEventName: string, time: number, handler: (event: ControllerEvent) => void, silent: boolean = false) {
    this.sub(this.eventBus().subscribeWithDebounce(controllerEventName, time, handler, silent));
  }

  protected subscribeWithDebounceMain(mainEventName: string, time: number, handler: (event: MainEvent) => void) {
    this.sub(this.eventBusMain().subscribeWithDebounce(mainEventName, time, handler));
  }

  protected subscribeWithFilter(controllerEventName: string,
                                handler: (event: ControllerEvent) => void,
                                filter: (event: ControllerEvent) => boolean,
                                silent: boolean = false) {
    this.sub(this.eventBus().subscribeWithFilter(controllerEventName, handler, filter, silent));
  }

  protected subscribeWindowResize(debounce: number, handler: (event: Event) => void) {
    this.sub(fromEvent(this._window, 'resize').pipe(debounceTime(debounce), distinctUntilChanged()).subscribe((event) => {
      try {
        handler(event);
      }
      catch(e) {
        // ignore
      }
    }));
  }

  protected screenSizeAware(): void {
    this.subscribeWindowResize(100, (event) => {
      this.checkMobileView();
      this.processScreenWidth(this.getWindowWidth());
    });

    this.checkMobileView();
    this.processScreenWidth(this.getWindowWidth());
  }

  protected checkMobileView(): void {
    const width = this.getWindow().innerWidth;

    if(width < CommonConstants.mobileWidthThreshold) {
      this.enterMobileView();
    }
    else {
      this.exitMobileView();
    }
  }

  protected enterMobileView(): void {
    // empty
  }

  protected exitMobileView(): void {
    // empty
  }

  protected processScreenWidth(width: number): void {
    // empty
  }

  handle(eventName: string, eventData: any): any {
    try {
      const handlerName = `handle${eventName}`;
      const handlerObj = this as StringIndex;
      const handler = handlerObj[handlerName];

      if(typeof handler === 'function') {
        console.debug(`Handling view event: ${eventName}`);
        return handlerObj[handlerName](eventData);
      }
      else {
        console.warn(`No handler found for view event: ${eventName}`);
      }
    }
    catch(error: any) {
      console.error(`Failed to handle view event ${eventName}. Error: ${error}`);
      console.error(error.stack);
    }

    return null;
  }

  protected eventBus(): ControllerEventBus {
    if(this.controllerEventBus) {
      return this.controllerEventBus;
    }
    else {
      throw new Error('No controller event bus is available');
    }
  }

  protected eventBusMain(): MainEventBus {
    if(this.mainEventBus) {
      return this.mainEventBus;
    }
    else {
      throw new Error('No main event bus is available');
    }
  }

  protected sub(subscription: Subscription): void {
    this.subscriptions.push(subscription);
  }

  protected fetchQueryParams(route: ActivatedRoute, paramNames: Array<string>): Observable<{[key: string]: any}> {
    return new Observable<any>((observer) => {
      route.queryParams.subscribe((params) => {
        const results: {[key: string]: any} = {};
        paramNames.forEach((paramName) => {
          if(params.hasOwnProperty(paramName)) {
            results[paramName] = params[paramName];
          }
        });

        observer.next(results);
        observer.complete();
      });
    });
  }

  protected fetchRouteParam(route: ActivatedRoute, paramName: string): Observable<any> {
    return new Observable<any>((observer) => {
      route.params.subscribe((params) => {
        if(params.hasOwnProperty(paramName)) {
          const paramValue = params[paramName];
          observer.next(paramValue);
          observer.complete();
        }
        else {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  protected fetchRouteParams(route: ActivatedRoute, paramNames: Array<string>): Observable<{[key: string]: any}> {
    return new Observable<{[key: string]: any}>((observer) => {
      route.params.subscribe((params) => {
        const results: {[key: string]: any} = {};
        paramNames.forEach((paramName) => {
          if(params.hasOwnProperty(paramName)) {
            results[paramName] = params[paramName];
          }
        });

        observer.next(results);
        observer.complete();
      });
    });
  }

  protected isLoggedIn(): boolean {
    return this._userSessionRepository.isLoggedIn();
  }

  protected isSsoUser(): boolean {
    const userTokens = this._userTokensRepository.getUserTokens();

    if(userTokens && userTokens.sso) {
      return true;
    }
    else {
      return false;
    }
  }

  protected getCurrentUser(): UserSession {
    const userSession = this._userSessionRepository.getUserSession();

    if(!userSession) {
      throw new Error('No active user session');
    }

    return userSession;
  }

  protected getCurrentUserId(): number {
    const userSession = this._userSessionRepository.getUserSession();

    if(!userSession || userSession.userId === undefined || userSession.userId === null) {
      throw new Error('No active user session');
    }
    else {
      return userSession.userId!;
    }
  }

  protected getCurrentUserEmail(): string {
    const userSession = this._userSessionRepository.getUserSession();

    if(!userSession || userSession.email === undefined || userSession.email === null) {
      throw new Error('No active user session');
    }
    else {
      return userSession.email;
    }
  }

  protected isCurrentUserSee(): boolean {
    const userSession = this._userSessionRepository.getUserSession();

    if(!userSession || !userSession.userType) {
      throw new Error('No active user session');
    }
    else {
      return userSession.userType === 'see';
    }
  }

  protected isCurrentUserExternal(): boolean {
    return !this.isCurrentUserSee();
  }

  protected getCurrentUserLangCode(): string {
    const userSession = this._userSessionRepository.getUserSession();

    if(!userSession || userSession.userId === undefined || userSession.userId === null) {
      throw new Error('No active user session');
    }
    else {
      return userSession.languageCode || 'en';
    }
  }

  protected translate(token: string): string {
    return this._translateService.instant(token);
  }

  protected hasAccess(accessName: string, parentAccessName: string|null = null): boolean {
    return this._accessControlService.checkUserAccess(accessName, parentAccessName);
  }

  protected navigate(url: string, event: any = null): Promise<boolean> {
    if(event && (event.ctrlKey || event.metaKey)) {
      this.getWindow().open(url);
      return Promise.resolve(true);
    }
    else {
      return this._router.navigateByUrl(url);
    }
  }

  protected navigateWithQueryParams(url: string, queryParams: {[key: string]: string|number|boolean}): void {
    this._router.navigate([url], {queryParams: queryParams});
  }

  protected getCurrentRoute(): ActivatedRoute {
    return this._route;
  }

  protected getCurrentUrl(): string {
    let url = '/';

    if(this._route.parent) {
      const parentUrl = this._route.parent.snapshot.url.map(s => s.path).join('/');
      url += parentUrl;
    }

    const childUrl = this._route.snapshot.url.map(s => s.path).join('/');

    if(childUrl && childUrl.length > 0) {
      url += '/';
      url += childUrl;
    }

    return url;
  }

  protected errorToast(summary: string, detail?: string): void {
    this._toastMessageService.showError(summary, detail);
  }

  protected messageToast(summary: string, detail?: string): void {
    this._toastMessageService.showMessage(summary, detail);
  }

  protected successToast(summary: string, detail?: string): void {
    this._toastMessageService.showSuccess(summary, detail);
  }

  protected warningToast(summary: string, detail?: string): void {
    this._toastMessageService.showWarning(summary, detail);
  }

  protected getWindowWidth(): number {
    return this.getWindow().innerWidth;
  }

  ngOnDestroy() {
    if(this.subscriptions.length > 0) {
      this.subscriptions.forEach((s) => s.unsubscribe());
      this.subscriptions = [];
    }
  }
}
